import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { IFieldFactoryProps } from '@sravni/cosago-react-library/lib/types';
import { Autocomplete, Typography } from '@sravni/react-design-system';
import type { SelectValue } from '@sravni/react-design-system/dist/types/types/Select.types';
import { useDebounceValue } from '@sravni/react-utils';
import { useCallback, useEffect, useRef, useState } from 'react';

import { sendEventFillFormField } from 'shared/lib/sendGAEvents';

import { type TWorkplaceFormFields, useDictionaries, useGetJobPositions } from 'entities/creditForm';

import { WorkplaceInfoTexts } from 'features/UpdateWorkplaceInfo/WorkplaceInfo.texts';

import styles from './JobTitle.module.scss';

export const JobTitle: FC<IFieldFactoryProps> = ({ type, label = '' }) => {
  const { jobPositions } = useDictionaries(['jobPositions']);
  const [getJobPositions, { isLoading }] = useGetJobPositions();

  const { setValue, formState, watch, clearErrors, trigger } = useFormContext<TWorkplaceFormFields>();
  const { errors } = formState;
  const errorText = errors.jobTitle?.message;
  const jobTitleValue = watch('jobTitle.value');

  const [searchValue, setSearchValue] = useState(jobTitleValue);
  const debounceSearchValue = useDebounceValue(searchValue, 500);
  const prevDebounceSearchValueRef = useRef<string | null>(debounceSearchValue);

  const getJobPositionsSuggests = useCallback(async (value: string) => getJobPositions(value, true), [getJobPositions]);

  // Получаем подсказки при изменении значения в поле, с учетом debounce для оптимизации количества запросов
  useEffect(() => {
    if (debounceSearchValue === prevDebounceSearchValueRef.current) {
      return;
    }
    prevDebounceSearchValueRef.current = debounceSearchValue;
    getJobPositionsSuggests(debounceSearchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchValue]);

  // Синхронизируем значение поля с внешним значением из формы, если оно изменится
  useEffect(() => {
    if (!jobTitleValue && searchValue !== '') {
      prevDebounceSearchValueRef.current = null;
      setSearchValue('');
      return;
    }

    if (jobTitleValue && searchValue !== jobTitleValue) {
      prevDebounceSearchValueRef.current = null;
      setSearchValue(jobTitleValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobTitleValue]);

  const clearSelectionData = useCallback(() => {
    clearErrors('jobTitle');
    setSearchValue('');
    setValue('jobTitle', null);
    trigger('jobTitle');
  }, [setValue, clearErrors, trigger]);

  const onSelectOption = useCallback(
    (selectValue?: SelectValue) => {
      const value = typeof selectValue === 'string' ? selectValue : selectValue?.toString();

      const selectedSuggestion = jobPositions.find(
        (suggest) => suggest.value.toLowerCase().trim() === value?.toLowerCase().trim(),
      );

      if (!value || !selectedSuggestion) {
        clearSelectionData();
        return;
      }

      setSearchValue(value);
      sendEventFillFormField(4, WorkplaceInfoTexts.jobTitle, true);
      setValue('jobTitle', selectedSuggestion);
      trigger('jobTitle');
    },
    [setValue, clearSelectionData, jobPositions, trigger],
  );

  const onBLur = useCallback(() => {
    if (jobTitleValue && searchValue !== jobTitleValue) {
      setSearchValue(jobTitleValue);
    }
  }, [jobTitleValue, searchValue]);

  return (
    <div className={styles.inputWrapper}>
      <Autocomplete
        icon={null}
        label={label}
        name={type}
        options={jobPositions}
        value={searchValue}
        onChange={setSearchValue}
        loading={isLoading}
        onClear={clearSelectionData}
        onSelect={onSelectOption}
        onBlur={onBLur}
        invalid={!!errorText}
      />
      {errorText && (
        <Typography.Text
          size={12}
          className={styles.errorText}
        >
          {errorText}
        </Typography.Text>
      )}
    </div>
  );
};
